import {
  FormControl,
  Flex,
  Box,
  FormLabel,
  Grid,
  RadioGroup,
  Radio,
  Stack,
} from '@chakra-ui/react';
import { useAppSelector, useAppDispatch } from '../../../store/redux/hooks';
import {
  setFocusedField,
  setWatermarkProp,
} from '../../../store/redux/slices/template.slice';
import { LabelWithTooltip } from './LabelWithTooltip';
import { Platform } from '../../../store/types/template';
import { useRef } from 'react';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';

const WatermarkFields = ({ platform }: { platform: Platform }) => {
  const watermarkColor = useAppSelector(
    (s) => s.template.images[platform].watermarkColor
  );
  const watermarkPosition = useAppSelector(
    (s) => s.template.images[platform].watermarkPosition
  );

  const dispatch = useAppDispatch();

  const watermarkColorRef = useRef<HTMLDivElement>(null);
  const watermarkPositionRef = useRef<HTMLDivElement>(null);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    prefix: `design.${platform}`,
    fieldRefMap: {
      watermarkColor: watermarkColorRef,
      watermarkPosition: watermarkPositionRef,
    },
  });

  const watermarkColorOptions = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
  ];

  const watermarkPositionOptions = [
    { label: 'Top left', value: 'topLeft' },
    { label: 'Top right', value: 'topRight' },
    { label: 'Bottom left', value: 'bottomLeft' },
    { label: 'Bottom right', value: 'bottomRight' },
  ];

  const handleFocus = (field: string) => {
    dispatch(setFocusedField(`design.${platform}.${field}`));
  };

  return (
    <Box>
      <LabelWithTooltip
        label="PassEntry Watermark"
        tooltip="The watermark can’t be removed on your current plan. Please contact your account manager at sales@passentry.com to upgrade."
      />

      <Flex gap={6} mt={4} direction={{ lg: 'row', base: 'column' }}>
        <FieldEditIndicator
          fieldRef={watermarkColorRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="67px"
          width="50%"
        >
          <Box>
            <FormControl>
              <FormLabel variant="templateBuilderLabel">Colour</FormLabel>
              <RadioGroup
                my={4}
                value={watermarkColor}
                onChange={(value) =>
                  dispatch(
                    setWatermarkProp({
                      platform: platform,
                      field: 'watermarkColor',
                      value: value,
                    })
                  )
                }
                onFocus={() => handleFocus('watermarkColor')}
              >
                <Stack direction="column" gap={4}>
                  {watermarkColorOptions.map((type) => (
                    <Radio key={type.value} value={type.value}>
                      {type.label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
          </Box>
        </FieldEditIndicator>
        <FieldEditIndicator
          fieldRef={watermarkPositionRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-45px"
          top="67px"
          width="50%"
        >
          <Box>
            <FormControl>
              <FormLabel variant="templateBuilderLabel">Position</FormLabel>
              <RadioGroup
                my={4}
                value={watermarkPosition}
                onChange={(value) =>
                  dispatch(
                    setWatermarkProp({
                      platform: platform,
                      field: 'watermarkPosition',
                      value: value,
                    })
                  )
                }
                onFocus={() => handleFocus('watermarkPosition')}
              >
                <Grid
                  templateColumns={{ xl: 'repeat(2, 1fr)', base: '1fr' }}
                  columnGap={4}
                  rowGap={4}
                >
                  {watermarkPositionOptions.map((type) => (
                    <Radio key={type.value} value={type.value}>
                      {type.label}
                    </Radio>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>
          </Box>
        </FieldEditIndicator>
      </Flex>
    </Box>
  );
};

export default WatermarkFields;
